import type { AgeType, TaxCategory } from '@eti/schema-types';
import type {
  AncillaryBundleType,
  ExtraProductsType,
  TravelerPricesType,
  TravelersType,
} from '../../../models';
import type { CarrierPriceType } from './price-specification/CarrierPriceList';

type TravelerData = {
  ageType: AgeType;
  id: string;
  markup: number;
  price: number;
  providerPricePerCarrier: CarrierPriceType[];
  taxesAndFees: TaxCategory[];
  vat: number;
};

export type TravelerDisplayPricesType = {
  ageType: AgeType;
  markup: number;
  price: number;
  providerPricePerCarrier: CarrierPriceType[];
  quantity: number;
  taxesAndFees: TaxCategory[];
  totalPrice: number;
  totalPriceSum: number;
  vat: number;
};

export type OrderExtraProductType = {
  id: string;
  code: string;
  name: string;
  productSummaryAlternativeName: string;
  providerPricePerCarrier?: { carrierName: string; providerPrice: number }[];
  title: string;
  totalPrice: number;
};

export const getTravelersDisplayPrices = (
  travelers: NonNullable<TravelersType> = [],
  travelerPrices: NonNullable<TravelerPricesType> = [],
  shouldSubtractMarkup = false,
): TravelerDisplayPricesType[] => {
  const travelersPerAgeType = travelers.reduce(
    (acc, traveler, index) => {
      if (!traveler) {
        return acc;
      }
      const priceInfo = travelerPrices.find(
        (travelerPrice) => String(travelerPrice?.travelerId) === traveler.id,
      );

      if (!priceInfo) {
        return acc;
      }

      const providerPricePerCarrier =
        priceInfo.carrierPriceBreakdowns && priceInfo.carrierPriceBreakdowns.length > 0
          ? priceInfo.carrierPriceBreakdowns.map((item) => ({
              carrierName: item.carrier.name as string,
              providerPrice: item.providerPrice.value,
              vat: item.vat.value,
            }))
          : [];

      const travelerData: TravelerData = {
        ageType: traveler.ageType,
        providerPricePerCarrier,
        id: String(traveler.id || index),
        markup: priceInfo.price?.markup?.value ?? 0,
        price: priceInfo.price?.price?.value ?? 0,
        taxesAndFees: priceInfo.taxesAndFees as TaxCategory[],
        vat: priceInfo.price?.vat?.value ?? 0,
      };

      if (!acc[travelerData.ageType]) {
        acc[travelerData.ageType] = [];
      }
      acc[travelerData.ageType].push(travelerData);

      return acc;
    },
    {} as Record<AgeType, TravelerData[]>,
  );

  return Object.entries(travelersPerAgeType).map(([ageType, travelersOfAgeType]) => {
    const totalMarkup = travelersOfAgeType.reduce((sum, traveler) => sum + traveler.markup, 0);
    const totalPrice = travelersOfAgeType.reduce((sum, traveler) => sum + traveler.price, 0);

    const { vat, taxesAndFees, markup, price } = travelersOfAgeType[0];

    const providerCost = price - (vat + markup);

    return {
      ageType,
      markup,
      providerPricePerCarrier: travelersOfAgeType[0].providerPricePerCarrier,
      price: providerCost,
      quantity: travelersOfAgeType.length,
      totalPrice: shouldSubtractMarkup ? price - markup : price,
      totalPriceSum: shouldSubtractMarkup ? totalPrice - totalMarkup : totalPrice,
      vat,
      taxesAndFees,
    } as TravelerDisplayPricesType;
  });
};

export const getExtraProducts = (
  extraProducts: ExtraProductsType = [],
  shouldSubtractMarkup = false,
  productsWithoutMarkup: string[] = [],
  marketingCarrierNames: string | null,
  shouldShowCarrierPricesBreakdown: boolean,
): OrderExtraProductType[] | null => {
  if (!extraProducts) {
    return null;
  }

  return extraProducts.reduce((acc: OrderExtraProductType[] = [], extraProduct) => {
    if (extraProduct && extraProduct.id) {
      const markup = extraProduct.price.markup?.value ?? 0;
      const price = extraProduct.price.price.value;
      const shouldDisplayProductWithoutMarkup =
        shouldSubtractMarkup && productsWithoutMarkup.includes(String(extraProduct.id));

      const getProductTitle = () => {
        const hasCarrierBreakdowns =
          shouldShowCarrierPricesBreakdown && extraProduct.ancillaryCarrierPriceBreakdowns?.length;

        if (shouldDisplayProductWithoutMarkup && hasCarrierBreakdowns) {
          return extraProduct.productSummaryAlternativeName || extraProduct.name;
        }
        if (shouldDisplayProductWithoutMarkup && marketingCarrierNames) {
          return `${
            extraProduct.productSummaryAlternativeName || extraProduct.name
          } ${marketingCarrierNames}`;
        }

        return extraProduct.productSummaryAlternativeName || extraProduct.name;
      };

      const providerPricePerCarrier = extraProduct.ancillaryCarrierPriceBreakdowns?.map((item) => ({
        carrierName: item.carrier.name as string,
        providerPrice: item.providerPrice.value,
      }));

      acc.push({
        id: extraProduct.id,
        code: extraProduct.code,
        name: extraProduct.name,
        productSummaryAlternativeName: extraProduct.productSummaryAlternativeName,
        providerPricePerCarrier,
        title: getProductTitle(),
        totalPrice: shouldDisplayProductWithoutMarkup ? price - markup : price,
      });
    }
    return acc;
  }, []);
};

export const getAncillaryBundleSelectedOptionProducts = (bundle: AncillaryBundleType) => {
  const selectedOption = bundle.options.find(
    (option) => option && option.id === bundle.selectedOption,
  );

  const selectedOptionProducts =
    selectedOption &&
    selectedOption.content &&
    selectedOption.content.reduce((acc: string[], product) => {
      if (product) {
        acc.push(product.name);
      }
      return acc;
    }, []);

  return { selectedOption, selectedOptionProducts };
};
